import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { catchError, from, map, of, switchMap, withLatestFrom } from 'rxjs';
import { State } from '../auth.state';
import { State as AppState } from '../../reducers';
import { FrontendApi } from '@ory/client';

import * as actions from './login.actions';
import { Injectable } from '@angular/core';
import { auth } from '../auth.selectors';
import { kratosErrorHandler } from '../kratos-error-handler';

@Injectable()
export class AuthLoginEffects {
  createLoginFlow = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CreateLoginFlow),
      switchMap((action) =>
        from(
          this.kratos.createBrowserLoginFlow({
            aal: action.aal,
            refresh: action.refresh,
            loginChallenge: action.login_challenge,
          })
        ).pipe(
          map((res) => actions.SuccessLoginFlow({ flow: res.data })),
          catchError((err) => of(actions.FailLoginFlow({ error: err.response.data })))
        )
      )
    )
  );

  updateLoginFlow = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestUpdateLoginFlow),
      withLatestFrom(this.store.pipe(select(auth.getLoginFlow))),
      switchMap(([action, flow]) =>
        from(
          this.kratos.updateLoginFlow({
            flow: flow.id,
            updateLoginFlowBody: action.data,
          })
        ).pipe(
          map((res) => actions.SuccessUpdateLoginFlow({ result: res.data })),
          catchError((err) => {
            const kratosKnownBehaviour = kratosErrorHandler({ error: err });
            if (kratosKnownBehaviour) {
              return of(kratosKnownBehaviour);
            }
            if (err.response.status === 400) {
              return of(actions.FailUpdateLoginFlow({ flow: err.response.data }));
            }
            return of(actions.FailUpdateLoginFlow({ error: err.response }));
          })
        )
      )
    )
  );

  /**
   * deprecated
   */
  doLogin = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestLoginAction),
      switchMap((action) =>
        from(
          this.kratos.createBrowserLoginFlow().then((flow) =>
            this.kratos.updateLoginFlow({
              flow: flow.data.id,
              updateLoginFlowBody: {
                identifier: action.email ?? action.phoneNumber,
                password: action.password,
                method: 'password',
                csrf_token: flow.data.ui.nodes.find((n) => n.attributes['name'] === 'csrf_token')
                  .attributes['value'],
              },
            })
          )
        ).pipe(
          map((res) =>
            actions.ResponseSuccessAuthLoginAction({
              session: res.data.session,
              userId: res.data.session.identity.metadata_public['ca_member_id'],
            })
          ),
          catchError((err) =>
            of(
              actions.ResponseFailAuthLoginAction({
                payload: {
                  status: err.response.status,
                },
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private kratos: FrontendApi
  ) {}
}
